/**
 * Bundle Builder
 * Handles product selection, variant changes, and cart integration
 * Uses the product-card snippet with bundle_mode: true
 */

(function() {
  'use strict';

  class BundleBuilder {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      
      // Discount settings
      this.enableDiscountPreview = section.dataset.enableDiscountPreview === 'true';
      this.discountType = section.dataset.discountType || 'percentage';
      this.discountPercentage = parseInt(section.dataset.discountPercentage) || 0;
      this.discountFixedAmount = parseFloat(section.dataset.discountFixedAmount) || 0;
      this.discountFixedAmountCents = this.discountFixedAmount * 100;
      
      // Minimum requirements for percentage/fixed discounts
      this.minimumRequirementType = section.dataset.minimumRequirementType || 'none';
      this.minimumPurchaseAmount = parseFloat(section.dataset.minimumPurchaseAmount) || 0;
      this.minimumPurchaseAmountCents = this.minimumPurchaseAmount * 100;
      this.minimumQuantity = parseInt(section.dataset.minimumQuantity) || 1;
      
      // Buy X Get Y settings
      this.buyXType = section.dataset.buyXType || 'quantity';
      this.buyXQuantity = parseInt(section.dataset.buyXQuantity) || 2;
      this.buyXAmount = parseFloat(section.dataset.buyXAmount) || 0;
      this.buyXAmountCents = this.buyXAmount * 100;
      this.getYQuantity = parseInt(section.dataset.getYQuantity) || 1;
      this.getYDiscountType = section.dataset.getYDiscountType || 'free';
      this.getYDiscountPercentage = parseInt(section.dataset.getYDiscountPercentage) || 50;
      this.getYDiscountAmount = parseFloat(section.dataset.getYDiscountAmount) || 0;
      this.getYDiscountAmountCents = this.getYDiscountAmount * 100;
      
      // State
      this.selectedProducts = new Map();
      
      // Elements - using product-card with bundle mode
      this.productCards = section.querySelectorAll('.product-card[data-bundle-product]');
      this.summaryItems = section.querySelector(`#bundle-items-${this.sectionId}`);
      this.summaryCount = section.querySelectorAll('.bundle-summary__count');
      this.summaryMin = section.querySelectorAll('.bundle-summary__min');
      this.summaryTotal = section.querySelectorAll('.bundle-summary__total-value');
      this.summarySubtotal = section.querySelectorAll('.bundle-summary__total-subtotal');
      this.summaryDiscountRow = section.querySelectorAll('.bundle-summary__total-row--discount');
      this.summaryDiscountValue = section.querySelectorAll('.bundle-summary__total-discount');
      this.summarySavings = section.querySelectorAll('.bundle-summary__savings');
      this.progressFill = section.querySelector('.bundle-summary__progress-fill');
      // Try multiple selectors for the add to cart button
      // CSS.escape handles special characters in section IDs (like --)
      const escapedId = CSS.escape ? CSS.escape(`bundle-add-to-cart-${this.sectionId}`) : `bundle-add-to-cart-${this.sectionId}`;
      this.addToCartBtn = section.querySelector(`#${escapedId}`) 
        || section.querySelector('.bundle-summary__add-btn-wrapper .btn')
        || section.querySelector('.bundle-summary__add-btn');
      
      // Mobile elements
      this.mobileBar = section.querySelector(`#bundle-mobile-bar-${this.sectionId}`);
      this.mobileCount = this.mobileBar?.querySelector('.bundle-mobile-bar__count');
      this.mobileSubtotal = this.mobileBar?.querySelector('.bundle-mobile-bar__subtotal');
      this.mobileDiscount = this.mobileBar?.querySelector('.bundle-mobile-bar__discount');
      this.mobileDiscountRow = this.mobileBar?.querySelector('.bundle-mobile-bar__total-row--discount');
      this.mobileTotal = this.mobileBar?.querySelector('.bundle-mobile-bar__total');
      this.drawer = section.querySelector(`#bundle-drawer-${this.sectionId}`);
      this.drawerItems = section.querySelector(`#bundle-drawer-items-${this.sectionId}`);
      this.drawerSubtotal = this.drawer?.querySelector('.bundle-drawer__subtotal');
      this.drawerDiscount = this.drawer?.querySelector('.bundle-drawer__discount');
      this.drawerDiscountRow = this.drawer?.querySelector('.bundle-drawer__total-row--discount');
      // Try multiple selectors for the drawer add to cart button
      const escapedDrawerId = CSS.escape ? CSS.escape(`bundle-drawer-add-to-cart-${this.sectionId}`) : `bundle-drawer-add-to-cart-${this.sectionId}`;
      this.drawerAddBtn = section.querySelector(`#${escapedDrawerId}`)
        || this.drawer?.querySelector('.bundle-drawer__add-btn')
        || this.drawer?.querySelector('.btn');
      
      this.init();
    }
    
    init() {
      this.bindEvents();
      this.initLucideIcons();
      // Initial UI update to set correct button states
      this.updateUI();
    }
    
    bindEvents() {
      // Product card events
      this.productCards.forEach(card => {
        // Toggle bundle buttons (both add and remove)
        const toggleBtns = card.querySelectorAll('[data-action="toggle-bundle"]');
        toggleBtns.forEach(btn => {
          btn.addEventListener('click', () => this.toggleProduct(card));
        });
        
        // Arco Select component - listen for select:change event
        const selectWrapper = card.querySelector('.product-card__bundle-variants [data-select]');
        if (selectWrapper) {
          selectWrapper.addEventListener('select:change', (e) => {
            this.handleVariantChange(card, e.detail?.value);
          });
          
          // Also listen for native select change (fallback)
          const nativeSelect = selectWrapper.querySelector('.select__native');
          if (nativeSelect) {
            nativeSelect.addEventListener('change', () => {
              this.handleVariantChange(card, nativeSelect.value);
            });
          }
        }
      });
      
      // Add to cart buttons
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => this.addAllToCart());
      }
      if (this.drawerAddBtn) {
        this.drawerAddBtn.addEventListener('click', () => this.addAllToCart());
      }
      
      // Mobile drawer
      const openDrawerBtns = this.section.querySelectorAll('[data-action="open-drawer"]');
      openDrawerBtns.forEach(btn => {
        btn.addEventListener('click', () => this.openDrawer());
      });
      
      const closeDrawerBtns = this.section.querySelectorAll('[data-action="close-drawer"]');
      closeDrawerBtns.forEach(btn => {
        btn.addEventListener('click', () => this.closeDrawer());
      });
    }
    
    toggleProduct(card) {
      const productId = card.dataset.productId;
      
      if (this.selectedProducts.has(productId)) {
        this.removeProduct(productId, card);
      } else {
        // No max limit enforcement - Shopify discounts use minimum, not maximum
        this.addProduct(card);
      }
    }
    
    addProduct(card) {
      const compareAtPrice = parseInt(card.dataset.productCompareAtPrice) || 0;
      const price = parseInt(card.dataset.productPrice);
      
      const productData = {
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle,
        price: price,
        compareAtPrice: compareAtPrice > price ? compareAtPrice : 0,
        image: card.dataset.productImage,
        handle: card.dataset.productHandle,
        variantTitle: this.getVariantTitle(card)
      };
      
      this.selectedProducts.set(productData.productId, productData);
      card.classList.add('is-selected');
      
      this.updateUI();
    }
    
    removeProduct(productId, card = null) {
      this.selectedProducts.delete(productId);
      
      if (!card) {
        card = this.section.querySelector(`.product-card[data-bundle-product][data-product-id="${productId}"]`);
      }
      
      if (card) {
        card.classList.remove('is-selected');
      }
      
      this.updateUI();
    }
    
    getVariantTitle(card) {
      // Get variant title from Arco select display value
      const selectValue = card.querySelector('.product-card__bundle-variants .select__value');
      if (selectValue) {
        return selectValue.textContent.trim();
      }
      
      // Fallback to native select
      const nativeSelect = card.querySelector('.product-card__bundle-variants .select__native');
      if (nativeSelect && nativeSelect.selectedIndex >= 0) {
        const option = nativeSelect.options[nativeSelect.selectedIndex];
        // Remove " - Sold out" suffix if present
        return option.text.replace(' - Sold out', '').trim();
      }
      
      return null;
    }
    
    handleVariantChange(card, variantId) {
      if (!variantId) return;
      
      // Get variant data from JSON
      const variantsJson = card.querySelector('.product-card__variants-json');
      if (!variantsJson) return;
      
      const variants = JSON.parse(variantsJson.textContent);
      const selectedVariant = variants.find(v => v.id == variantId);
      
      if (selectedVariant) {
        // Update card data attributes
        card.dataset.variantId = selectedVariant.id;
        card.dataset.productPrice = selectedVariant.price;
        card.dataset.productCompareAtPrice = selectedVariant.compare_at_price || 0;
        
        // Update image if variant has featured_image
        if (selectedVariant.featured_image) {
          const imageEl = card.querySelector('.product-card__image');
          if (imageEl) {
            // Use the variant's featured image URL
            const imageUrl = selectedVariant.featured_image.src;
            if (imageUrl) {
              // Shopify image URLs can be resized by modifying the URL
              imageEl.src = imageUrl.replace(/(\.[^.]+)$/, '_600x800_crop_center$1');
            }
          }
          // Update data attribute for bundle summary
          card.dataset.productImage = selectedVariant.featured_image.src;
        }
        
        // Update button state - check availability explicitly
        // available is true when: inventory not tracked, OR inventory > 0, OR oversell allowed
        const isAvailable = selectedVariant.available === true;
        const addBtn = card.querySelector('.product-card__bundle-btn--add');
        if (addBtn) {
          addBtn.disabled = !isAvailable;
          const btnText = addBtn.querySelector('.btn__text');
          if (btnText) {
            btnText.textContent = isAvailable ? 'Add to Bundle' : 'Out of Stock';
          }
        }
        
        // If product is already selected, update the selection
        if (this.selectedProducts.has(card.dataset.productId)) {
          const compareAtPrice = selectedVariant.compare_at_price || 0;
          const updatedProduct = {
            ...this.selectedProducts.get(card.dataset.productId),
            variantId: selectedVariant.id,
            price: selectedVariant.price,
            compareAtPrice: compareAtPrice > selectedVariant.price ? compareAtPrice : 0,
            variantTitle: this.getVariantTitle(card)
          };
          // Update image in selection if variant has one
          if (selectedVariant.featured_image && selectedVariant.featured_image.src) {
            updatedProduct.image = selectedVariant.featured_image.src;
          }
          this.selectedProducts.set(card.dataset.productId, updatedProduct);
          this.updateUI();
        }
      }
    }
    
    updateUI() {
      const count = this.selectedProducts.size;
      const { total, compareTotal } = this.calculateTotal();
      
      // Determine minimum requirements based on discount type
      const { minRequired, canAddToCart } = this.checkMinimumRequirements(count, total);
      const progress = minRequired > 0 ? Math.min((count / minRequired) * 100, 100) : 100;
      
      // Calculate discount (only applies when requirements are met)
      const { discountedTotal, discountAmount } = this.calculateDiscount(total, canAddToCart);
      const hasDiscount = discountAmount > 0;
      
      // Update counts
      this.summaryCount.forEach(el => el.textContent = count);
      this.summaryMin.forEach(el => el.textContent = minRequired);
      
      // Update subtotal
      this.summarySubtotal.forEach(el => el.textContent = this.formatMoney(total));
      
      // Update discount row
      this.summaryDiscountRow.forEach(el => {
        el.style.display = hasDiscount ? '' : 'none';
      });
      this.summaryDiscountValue.forEach(el => {
        el.textContent = '-' + this.formatMoney(discountAmount);
      });
      
      // Update savings indicator
      this.summarySavings.forEach(el => {
        el.style.display = hasDiscount ? '' : 'none';
        el.textContent = `You save ${this.formatMoney(discountAmount)}`;
      });
      
      // Update final total (discounted if applicable)
      this.summaryTotal.forEach(el => el.textContent = this.formatMoney(discountedTotal));
      
      // Update progress bar
      if (this.progressFill) {
        this.progressFill.style.width = `${progress}%`;
      }
      
      // Update add to cart buttons
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !canAddToCart;
        // Also toggle the CSS class for visual state
        this.addToCartBtn.classList.toggle('btn--disabled', !canAddToCart);
      }
      if (this.drawerAddBtn) {
        this.drawerAddBtn.disabled = !canAddToCart;
        // Also toggle the CSS class for visual state
        this.drawerAddBtn.classList.toggle('btn--disabled', !canAddToCart);
      }
      
      // Update mobile bar - show/hide based on item count
      if (this.mobileBar) {
        if (count > 0) {
          this.mobileBar.classList.remove('is-hidden');
        } else {
          this.mobileBar.classList.add('is-hidden');
        }
      }
      
      // Update mobile bar totals
      if (this.mobileSubtotal) {
        this.mobileSubtotal.textContent = this.formatMoney(total);
      }
      if (this.mobileDiscountRow) {
        this.mobileDiscountRow.style.display = hasDiscount ? '' : 'none';
      }
      if (this.mobileDiscount) {
        this.mobileDiscount.textContent = `-${this.formatMoney(discountAmount)}`;
      }
      if (this.mobileTotal) {
        this.mobileTotal.textContent = this.formatMoney(discountedTotal);
      }
      
      // Update drawer totals
      if (this.drawerSubtotal) {
        this.drawerSubtotal.textContent = this.formatMoney(total);
      }
      if (this.drawerDiscountRow) {
        this.drawerDiscountRow.style.display = hasDiscount ? '' : 'none';
      }
      if (this.drawerDiscount) {
        this.drawerDiscount.textContent = `-${this.formatMoney(discountAmount)}`;
      }
      
      // Update items lists
      this.renderItemsList(this.summaryItems);
      this.renderItemsList(this.drawerItems);
    }
    
    /**
     * Check minimum requirements based on discount type
     * Returns the minimum required quantity/amount and whether requirements are met
     */
    checkMinimumRequirements(itemCount, subtotal) {
      // Must have at least 1 item
      if (itemCount === 0) {
        return { minRequired: 1, canAddToCart: false };
      }
      
      // If discount preview is disabled, allow adding with at least 1 item
      if (!this.enableDiscountPreview) {
        return { minRequired: 1, canAddToCart: true };
      }
      
      // Check based on discount type
      if (this.discountType === 'percentage' || this.discountType === 'fixed') {
        // For percentage/fixed discounts, check minimum requirement type
        if (this.minimumRequirementType === 'quantity') {
          return { 
            minRequired: this.minimumQuantity, 
            canAddToCart: itemCount >= this.minimumQuantity 
          };
        } else if (this.minimumRequirementType === 'amount') {
          // For amount-based, we show progress but can add anytime (discount applies when threshold met)
          return { 
            minRequired: 1, 
            canAddToCart: true 
          };
        } else {
          // No minimum requirements
          return { minRequired: 1, canAddToCart: true };
        }
      } else if (this.discountType === 'buy_x_get_y') {
        // For Buy X Get Y, check based on buy type
        if (this.buyXType === 'quantity') {
          // Need at least buy_x + get_y items for the discount to apply
          const minForDiscount = this.buyXQuantity + this.getYQuantity;
          return { 
            minRequired: minForDiscount, 
            canAddToCart: itemCount >= minForDiscount 
          };
        } else if (this.buyXType === 'amount') {
          // For amount-based, can add anytime
          return { minRequired: 1, canAddToCart: true };
        }
      }
      
      // Default: allow with at least 1 item
      return { minRequired: 1, canAddToCart: true };
    }
    
    calculateDiscount(subtotal, meetsMinimum) {
      // Check if discount preview is enabled
      if (!this.enableDiscountPreview) {
        return { discountedTotal: subtotal, discountAmount: 0 };
      }
      
      // Only apply discount if minimum products requirement is met (from Bundle Settings)
      if (!meetsMinimum) {
        return { discountedTotal: subtotal, discountAmount: 0 };
      }
      
      const itemCount = this.selectedProducts.size;
      let discountAmount = 0;
      
      if (this.discountType === 'percentage' || this.discountType === 'fixed') {
        // Check minimum requirements for percentage/fixed discounts
        if (this.minimumRequirementType === 'amount' && subtotal < this.minimumPurchaseAmountCents) {
          return { discountedTotal: subtotal, discountAmount: 0 };
        }
        if (this.minimumRequirementType === 'quantity' && itemCount < this.minimumQuantity) {
          return { discountedTotal: subtotal, discountAmount: 0 };
        }
        
        if (this.discountType === 'percentage') {
          // Percentage discount (1-100)
          if (this.discountPercentage > 0) {
            discountAmount = Math.round(subtotal * (this.discountPercentage / 100));
          }
        } else if (this.discountType === 'fixed') {
          // Fixed amount discount
          if (this.discountFixedAmountCents > 0) {
            discountAmount = Math.min(this.discountFixedAmountCents, subtotal);
          }
        }
      } else if (this.discountType === 'buy_x_get_y') {
        // Buy X Get Y discount
        discountAmount = this.calculateBuyXGetYDiscount(subtotal, itemCount);
      }
      
      const discountedTotal = subtotal - discountAmount;
      
      return { discountedTotal, discountAmount };
    }
    
    calculateBuyXGetYDiscount(subtotal, itemCount) {
      // Get all selected product prices and sort ascending (cheapest first)
      const prices = [];
      this.selectedProducts.forEach(product => {
        prices.push(product.price);
      });
      prices.sort((a, b) => a - b);
      
      // Determine if customer qualifies based on buy type
      let qualifies = false;
      let completeBundles = 0;
      
      if (this.buyXType === 'quantity') {
        // Customer must buy X quantity
        const bundleSize = this.buyXQuantity + this.getYQuantity;
        completeBundles = Math.floor(itemCount / bundleSize);
        qualifies = completeBundles > 0;
      } else if (this.buyXType === 'amount') {
        // Customer must spend X amount
        // For amount-based, we give the discount once per threshold met
        if (subtotal >= this.buyXAmountCents) {
          completeBundles = Math.floor(subtotal / this.buyXAmountCents);
          qualifies = true;
        }
      }
      
      if (!qualifies) {
        return 0;
      }
      
      // Calculate discount for the Y items
      const totalDiscountedItems = completeBundles * this.getYQuantity;
      let discountAmount = 0;
      
      for (let i = 0; i < totalDiscountedItems && i < prices.length; i++) {
        const itemPrice = prices[i];
        
        if (this.getYDiscountType === 'free') {
          // Item is free
          discountAmount += itemPrice;
        } else if (this.getYDiscountType === 'percentage') {
          // Percentage off the item
          discountAmount += Math.round(itemPrice * (this.getYDiscountPercentage / 100));
        } else if (this.getYDiscountType === 'amount_off') {
          // Fixed amount off each item
          discountAmount += Math.min(this.getYDiscountAmountCents, itemPrice);
        }
      }
      
      return discountAmount;
    }
    
    renderItemsList(container) {
      if (!container) return;
      
      if (this.selectedProducts.size === 0) {
        const emptyText = this.section.querySelector('.bundle-summary__empty')?.textContent || 'Select products to build your bundle';
        container.innerHTML = `<p class="bundle-summary__empty">${emptyText}</p>`;
        return;
      }
      
      let html = '';
      let index = 0;
      this.selectedProducts.forEach((product, productId) => {
        // Build display title with variant
        const displayTitle = product.variantTitle 
          ? `${product.title} - ${product.variantTitle}`
          : product.title;
        
        // Build price HTML with compare at price support
        let priceHtml = '';
        if (product.compareAtPrice && product.compareAtPrice > product.price) {
          priceHtml = `
            <span class="bundle-summary__item-price--sale">${this.formatMoney(product.price)}</span>
            <span class="bundle-summary__item-price--compare">${this.formatMoney(product.compareAtPrice)}</span>
          `;
        } else {
          priceHtml = `<span>${this.formatMoney(product.price)}</span>`;
        }
        
        html += `
          <div class="bundle-summary__item" data-product-id="${productId}" style="--item-index: ${index}">
            <a href="/products/${product.handle}" class="bundle-summary__item-media">
              ${product.image 
                ? `<img src="${product.image}" alt="${product.title}" width="80" height="80" loading="lazy" class="bundle-summary__item-image">`
                : `<div class="bundle-summary__item-placeholder"><span data-lucide="package"></span></div>`
              }
            </a>
            <div class="bundle-summary__item-details">
              <a href="/products/${product.handle}" class="bundle-summary__item-title-wrapper">
                <span class="bundle-summary__item-title">
                  <span class="bundle-summary__item-title-text">${displayTitle}</span>
                </span>
              </a>
              <div class="bundle-summary__item-price">${priceHtml}</div>
            </div>
            <button 
              type="button" 
              class="bundle-summary__item-remove" 
              data-action="remove-item"
              data-product-id="${productId}"
              aria-label="Remove ${product.title}"
            >
              <span data-lucide="x"></span>
            </button>
          </div>
        `;
        index++;
      });
      
      container.innerHTML = html;
      
      // Initialize Lucide icons for the new elements
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
      
      // Bind remove buttons
      container.querySelectorAll('[data-action="remove-item"]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.removeProduct(btn.dataset.productId);
        });
      });
    }
    
    calculateTotal() {
      let total = 0;
      let compareTotal = 0;
      this.selectedProducts.forEach(product => {
        total += product.price;
        // Use compare_at_price if available, otherwise use price
        compareTotal += product.compareAtPrice > 0 ? product.compareAtPrice : product.price;
      });
      return { total, compareTotal };
    }
    
    formatMoney(cents) {
      if (window.formatMoney) {
        return window.formatMoney(cents, window.bundleBuilderMoneyFormat || window.themeMoneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }
    
    
    async addAllToCart() {
      const { total } = this.calculateTotal();
      const { canAddToCart } = this.checkMinimumRequirements(this.selectedProducts.size, total);
      console.log('[BundleBuilder] addAllToCart called', { count: this.selectedProducts.size, total, canAddToCart });
      if (!canAddToCart) {
        console.warn('[BundleBuilder] Cannot add to cart - minimum requirements not met');
        return;
      }
      
      const items = [];
      this.selectedProducts.forEach(product => {
        items.push({
          id: parseInt(product.variantId, 10),
          quantity: 1
        });
      });
      
      console.log('[BundleBuilder] Adding items to cart:', JSON.stringify(items));
      
      // Disable buttons during request
      if (this.addToCartBtn) this.addToCartBtn.disabled = true;
      if (this.drawerAddBtn) this.drawerAddBtn.disabled = true;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items })
        });
        
        const result = await response.json();
        console.log('[BundleBuilder] Cart response:', response.status, result);
        
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Failed to add to cart');
        }
        
        // Success - clear selections and update cart
        this.clearSelections();
        this.closeDrawer();
        
        // Fetch full cart for the event (cart/add.js doesn't return the full cart)
        var cart = null;
        if (window.CartAPI) {
          cart = await window.CartAPI.getCart();
        }
        
        // Trigger cart item added event with proper shape
        window.dispatchEvent(new CustomEvent('cart:item-added', { 
          detail: { 
            item: result.items ? result.items[0] : result,
            cart: cart
          } 
        }));
        
      } catch (error) {
        console.error('[BundleBuilder] Error adding to cart:', error);
        alert('There was an error adding items to your cart. Please try again.');
      } finally {
        // Re-enable buttons based on current requirements
        const currentTotal = this.calculateTotal().total;
        const { canAddToCart: canAdd } = this.checkMinimumRequirements(this.selectedProducts.size, currentTotal);
        if (this.addToCartBtn) this.addToCartBtn.disabled = !canAdd;
        if (this.drawerAddBtn) this.drawerAddBtn.disabled = !canAdd;
      }
    }
    
    clearSelections() {
      this.selectedProducts.clear();
      this.productCards.forEach(card => {
        card.classList.remove('is-selected');
      });
      this.updateUI();
    }
    
    openDrawer() {
      if (this.drawer) {
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }
    }
    
    closeDrawer() {
      if (this.drawer) {
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }
    
    initLucideIcons() {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
  }
  
  // Initialize all bundle builders on page
  function initBundleBuilders() {
    const sections = document.querySelectorAll('.bundle-builder');
    sections.forEach(section => {
      new BundleBuilder(section);
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleBuilders);
  } else {
    initBundleBuilders();
  }
  
  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.bundle-builder');
    if (section) {
      new BundleBuilder(section);
    }
  });
})();
